import { byPrefixAndName } from '@awesome.me/kit-39de79544e/icons'
import { faEnvelope, faLighthouse, faMouse } from '@fortawesome/pro-light-svg-icons'
import { Box, Button, Card, Typography } from '@mui/material'
import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useCampaign } from '@/api/campaigns/get'
import CardHeader from '@/common/components/Card/CardHeader'
import { AssetType } from '@/types/campaigns'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import Bubble, { BubbleLoading } from './Bubble'

const formatPercentage = (value: number, total: number) => {
  if (total === 0) {
    return 0
  }

  return (((value || 0) / total) * 100).toFixed(0)
}

type CampaignMembersInteractionsProps = {
  campaignId: string
}

const CampaignMembersInteractions: FC<CampaignMembersInteractionsProps> = ({ campaignId }) => {
  const navigateTo = useNavigate()
  const { t } = useTranslation()

  const { data: campaignData, isPending } = useCampaign(campaignId)

  const i = useMemo(
    () =>
      campaignData?.asset_type !== AssetType.phishing_simulation
        ? campaignData?.asset_metadata?.include_quiz
          ? 4
          : 3
        : 5,
    [campaignData]
  )

  const completedAllQuizzesCount = useMemo(() => {
    if (!campaignData?.asset_metadata?.include_quiz) return 0

    const assetsWithQuizzes = campaignData.assets.filter(
      (asset) => asset.number_of_quiz_questions && asset.number_of_quiz_questions > 0
    )

    if (assetsWithQuizzes.length === 0) return 0

    const quizCompletionCounts = assetsWithQuizzes.map((asset) => {
      const assetEvents = campaignData.events_by_asset[asset._id]
      return assetEvents?.quiz_completed || 0
    })

    return Math.min(...quizCompletionCounts)
  }, [campaignData])

  return (
    <Card>
      <CardHeader title={t('campaignDashboard.campaignInteraction.title')}>
        <Button
          variant="outlined"
          size="small"
          onClick={() => navigateTo(`/campaign/${campaignId}/members-interactions`)}>
          {t('campaignDashboard.campaignInteraction.buttonDetailedList')}
        </Button>
      </CardHeader>

      <Typography variant="body2" color="text.secondary" mt={2} mb={4}>
        {campaignData?.asset_type === AssetType.phishing_simulation
          ? t('campaignDashboard.campaignInteraction.subtitleSimulation')
          : t('campaignDashboard.campaignInteraction.subtitleVideoTraining')}
      </Typography>

      <Box display="flex" justifyContent="space-between" margin={'16px auto'} maxWidth={`${i * 17}vw`}>
        {isPending || !campaignData ? (
          Array(3)
            .fill('')
            .map((_, index) => <BubbleLoading key={index} />)
        ) : (
          <>
            {campaignData?.asset_type === AssetType.phishing_simulation ? ( // Phishing Simulation - Single and Package
              <>
                <Bubble
                  Icon={faEnvelope}
                  main={formatPercentage(campaignData.events.email_open, campaignData.total_members)}
                  secondary={campaignData.events.email_open || 0}
                  label={t('campaignDashboard.campaignInteraction.openedMessage')}
                  tooltip={t('campaignDashboard.campaignInteraction.openedMessageTooltip')}
                />
                <Bubble
                  Icon={faMouse}
                  main={formatPercentage(campaignData.events.email_link_click, campaignData.total_members)}
                  secondary={campaignData.events.email_link_click || 0}
                  label={t('campaignDashboard.campaignInteraction.clickedMaliciousLink')}
                  tooltip={t('campaignDashboard.campaignInteraction.clickedMaliciousLinkTooltip')}
                />
                <Bubble
                  Icon={byPrefixAndName.fak['light-database-circle-plus'] as IconProp}
                  main={formatPercentage(campaignData.events.submit, campaignData.total_members)}
                  secondary={campaignData.events.submit || 0}
                  label={t('campaignDashboard.campaignInteraction.insertedData')}
                  tooltip={t('campaignDashboard.campaignInteraction.insertedDataTooltip')}
                />
                <Bubble
                  Icon={byPrefixAndName.fak['clickguard'] as IconProp}
                  main={formatPercentage(campaignData.events.feedback_completed, campaignData.total_members)}
                  secondary={campaignData.events.feedback_completed || 0}
                  label={t('campaignDashboard.campaignInteraction.microTrainingCompleted')}
                  tooltip={t('campaignDashboard.campaignInteraction.microTrainingCompletedTooltip')}
                />
                <Bubble
                  Icon={faLighthouse}
                  main={formatPercentage(campaignData.events.report, campaignData.total_members)}
                  secondary={campaignData.events.report || 0}
                  label={t('campaignDashboard.campaignInteraction.reportedAsPhishing')}
                  tooltip={t('campaignDashboard.campaignInteraction.reportedAsPhishingTooltip')}
                />
              </>
            ) : campaignData.assets.length > 1 ? ( // Not Phishing Simulation and Package - Video Training Package
              <>
                <Bubble
                  Icon={byPrefixAndName.fak['light-tv-circle-play'] as IconProp}
                  main={formatPercentage(
                    Math.max(
                      0,
                      ...Object.values(campaignData.events_by_asset).map((asset) => asset.training_opened || 0)
                    ),
                    campaignData.total_members
                  )}
                  secondary={
                    Math.max(
                      0,
                      ...Object.values(campaignData.events_by_asset).map((asset) => asset.training_opened || 0)
                    ) || 0
                  }
                  label={t('campaignDashboard.campaignInteraction.startedVideoPackage')}
                  tooltip={t('campaignDashboard.campaignInteraction.startedVideoTooltip')}
                />
                <Bubble
                  Icon={byPrefixAndName.fak['light-tv-circle-play'] as IconProp}
                  main={formatPercentage(
                    campaignData.events_for_all_asset.training_completed,
                    campaignData.total_members
                  )}
                  secondary={campaignData.events_for_all_asset.training_completed || 0}
                  label={t('campaignDashboard.campaignInteraction.completedAllVideos')}
                  tooltip={t('campaignDashboard.campaignInteraction.completedAllVideosTooltip')}
                />
                <Bubble
                  Icon={byPrefixAndName.fak['light-tv-circle-play'] as IconProp}
                  main={formatPercentage(
                    campaignData.total_members - (campaignData.events_for_all_asset.training_completed || 0),
                    campaignData.total_members
                  )}
                  secondary={campaignData.total_members - (campaignData.events_for_all_asset.training_completed || 0)}
                  label={t('campaignDashboard.campaignInteraction.didntCompleteAllVideos')}
                  tooltip={t('campaignDashboard.campaignInteraction.didntCompleteAllVideosTooltip')}
                />
                {campaignData.asset_metadata.include_quiz && (
                  <Bubble
                    Icon={byPrefixAndName.fak['light-block-question-circle-check'] as IconProp}
                    main={formatPercentage(completedAllQuizzesCount, campaignData.total_members)}
                    secondary={completedAllQuizzesCount}
                    label={t('campaignDashboard.campaignInteraction.completedAllQuizzes')}
                    tooltip={t('campaignDashboard.campaignInteraction.completedQuizTooltip')}
                  />
                )}
              </>
            ) : (
              // Not phishing simulation and not package - Single Video Training
              <>
                <Bubble
                  Icon={byPrefixAndName.fak['light-tv-circle-play'] as IconProp}
                  main={formatPercentage(
                    Math.max(
                      0,
                      ...Object.values(campaignData.events_by_asset).map((asset) => asset.training_opened || 0)
                    ),
                    campaignData.total_members
                  )}
                  secondary={
                    Math.max(
                      0,
                      ...Object.values(campaignData.events_by_asset).map((asset) => asset.training_opened || 0)
                    ) || 0
                  }
                  label={t('campaignDashboard.campaignInteraction.startedVideo')}
                  tooltip={t('campaignDashboard.campaignInteraction.startedVideoTooltip')}
                />
                <Bubble
                  Icon={byPrefixAndName.fak['light-tv-circle-check'] as IconProp}
                  main={formatPercentage(
                    Math.max(
                      0,
                      ...Object.values(campaignData.events_by_asset).map((asset) => asset.training_completed)
                    ),
                    campaignData.total_members
                  )}
                  secondary={
                    Math.max(
                      0,
                      ...Object.values(campaignData.events_by_asset).map((asset) => asset.training_completed)
                    ) || 0
                  }
                  label={t('campaignDashboard.campaignInteraction.completedVideo')}
                  tooltip={t('campaignDashboard.campaignInteraction.completedVideoTooltip')}
                />
                <Bubble
                  Icon={byPrefixAndName.fak['light-tv-circle-xmark'] as IconProp}
                  main={formatPercentage(
                    campaignData.total_members -
                      (Math.max(
                        0,
                        ...Object.values(campaignData.events_by_asset).map((asset) => asset.training_completed)
                      ) || 0),
                    campaignData.total_members
                  )}
                  secondary={
                    campaignData.total_members -
                    (Math.max(
                      0,
                      ...Object.values(campaignData.events_by_asset).map((asset) => asset.training_completed)
                    ) || 0)
                  }
                  label={t('campaignDashboard.campaignInteraction.didntCompleteVideo')}
                  tooltip={t('campaignDashboard.campaignInteraction.didntCompleteVideoTooltip')}
                />
                {campaignData.asset_metadata.include_quiz && (
                  <Bubble
                    Icon={byPrefixAndName.fak['light-block-question-circle-check'] as IconProp}
                    main={formatPercentage(
                      Math.max(0, ...Object.values(campaignData.events_by_asset).map((asset) => asset.quiz_completed)),
                      campaignData.total_members
                    )}
                    secondary={
                      Math.max(
                        0,
                        ...Object.values(campaignData.events_by_asset).map((asset) => asset.quiz_completed)
                      ) || 0
                    }
                    label={t('campaignDashboard.campaignInteraction.completedQuiz')}
                    tooltip={t('campaignDashboard.campaignInteraction.completedQuizTooltip')}
                  />
                )}
              </>
            )}
          </>
        )}
      </Box>
    </Card>
  )
}

export default CampaignMembersInteractions
